import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { SlideLayout, itemVariants } from '../ui/SlideLayout';
const pipelineStages = [{
  label: 'Attention',
  sub: 'Q·K^T / √d'
}, {
  label: 'Softmax',
  sub: 'Normalization'
}, {
  label: 'V Multiply',
  sub: 'Weighted sum'
}, {
  label: 'FFN Layer',
  sub: 'Up + Gate + Down'
}, {
  label: 'Output',
  sub: 'Token logits'
}];
export function AppendixA2Slide() {
  return <SlideLayout data-id="element-33" data-mp="2wop8qz5bdh2d">
      <motion.div variants={itemVariants} className="mb-12" data-id="element-34" data-mp="2wp8ruj6pabhz">
        <p className="text-brand-teal font-mono text-xs tracking-widest uppercase mb-3" data-id="element-35" data-mp="3bn15f5sosptj">
          Appendix A2
        </p>
        <h2 className="text-3xl md:text-4xl font-bold text-brand-text mb-4" data-id="element-36" data-mp="36o7pl6pxgmq7">
          Isochron Dataflow Pipeline
        </h2>
        <p className="text-lg text-brand-text-dim max-w-3xl" data-id="element-37" data-mp="1haujbby98omg">
          Modern GPUs use kernel fusion (FlashAttention, vLLM, TensorRT-LLM) to
          reduce overhead. FPGAs go further with{' '}
          <span className="text-brand-text font-medium" data-id="element-38" data-mp="20cuiuir9zo5h">spatial pipelines</span>{' '}
          — operators are physically wired end-to-end, not sequentially
          scheduled.
        </p>
      </motion.div>

      {/* Spatial Pipeline Diagram */}
      <motion.div variants={itemVariants} className="mb-12" data-id="element-39" data-mp="2e6lq1khjavy1">
        <p className="text-xs text-brand-text-muted font-mono uppercase tracking-wider mb-4" data-id="element-40" data-mp="2e86j15o7a6ts">
          FPGA Spatial Pipeline — Operators Wired in Hardware
        </p>
        <div className="flex items-center gap-2 overflow-x-auto pb-4" data-id="element-41" data-mp="2o2yjqid2x9ss">
          {pipelineStages.map((stage, i) => <Fragment key={stage.label} data-id="element-42" data-mp="2s40o1dlusc1m" data-mpi={i}>
              <div className="flex-shrink-0 bg-brand-bg-card border border-brand-teal/30 rounded-xl px-6 py-4 text-center min-w-[140px]" data-id="element-43" data-mp="2x5g7504h08hd">
                <div className="text-brand-teal font-bold text-sm mb-1" data-id="element-44" data-mp="xtwx0i7fku64">
                  {stage.label}
                </div>
                <div className="text-brand-text-muted text-xs font-mono" data-id="element-45" data-mp="12t01a0fl6wd1">
                  {stage.sub}
                </div>
              </div>
              {i < pipelineStages.length - 1 && <div className="flex-shrink-0 w-8 h-0.5 bg-brand-teal/40 relative" data-id="element-46" data-mp="215cfg91g2uok">
                  <ArrowRightIcon className="w-4 h-4 text-brand-teal/60 absolute -right-2 -top-[7px]" data-id="element-47" data-mp="2f0o7g8o7u71s" />
                </div>}
            </Fragment>)}
        </div>
      </motion.div>

      {/* Comparison */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-48" data-mp="2k23si0qe8dk6">
        <motion.div variants={itemVariants} className="bg-brand-bg-card border border-brand-border rounded-xl p-6" data-id="element-49" data-mp="2k1lryrwp7d6g">
          <h4 className="text-brand-teal font-semibold mb-2" data-id="element-50" data-mp="2tr5b7yobp91u">
            Zero Driver Overhead
          </h4>
          <p className="text-sm text-brand-text-dim" data-id="element-51" data-mp="37qn5gtke1eil">
            No OS, no driver, no kernel launch latency. Data flows through
            physical gates with no software intermediary.
          </p>
        </motion.div>
        <motion.div variants={itemVariants} className="bg-brand-bg-card border border-brand-border rounded-xl p-6" data-id="element-52" data-mp="3mfgatiw3n0rw">
          <h4 className="text-brand-teal font-semibold mb-2" data-id="element-53" data-mp="3cfz9fdnvr91q">
            Deterministic Cycle Control
          </h4>
          <p className="text-sm text-brand-text-dim" data-id="element-54" data-mp="3heiw5gaasov5">
            Every operation completes in a known number of clock cycles. No
            scheduling variance, no preemption.
          </p>
        </motion.div>
        <motion.div variants={itemVariants} className="bg-brand-bg-card border border-brand-border rounded-xl p-6" data-id="element-55" data-mp="38em18wxbetrn">
          <h4 className="text-brand-teal font-semibold mb-2" data-id="element-56" data-mp="2tnd7qcqako98">
            Arbitrary Operator Fusion
          </h4>
          <p className="text-sm text-brand-text-dim" data-id="element-57" data-mp="2korrio8allbm">
            Any sequence of operators can be fused into a single pipeline — no
            compiler constraints on what can be combined.
          </p>
        </motion.div>
      </div>
    </SlideLayout>;
}